import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import { Dialog } from "@mui/material";
import * as React from "react";

import { AlertDialog, PageLayout } from "@/shared/components";
import { AddToCollection } from "@/shared/components/collections";
import { EventsList } from "@/shared/components/events";
import { useNotification } from "@/shared/components/notifications/useNotification";
import { type SelectionOption } from "@/shared/components/ResourceView";
import { EventRecord } from "@/shared/domain/events/EventRecord";
import { useDeleteEvents } from "@/shared/domain/events/hooks";

export function EventsPage() {
  const { mutateAsync: deleteEvents } = useDeleteEvents();
  const showNotification = useNotification();

  const [addToCollectionOpen, setAddToCollectionOpen] = React.useState(false);
  const [selectedEventsForCollection, setSelectedEventsForCollection] =
    React.useState<Set<string>>(new Set());

  const [deleteConfirmOpen, setDeleteConfirmOpen] = React.useState(false);
  const [eventsToDelete, setEventsToDelete] = React.useState<string[]>([]);

  const selectionOptions: SelectionOption<EventRecord>[] = React.useMemo(
    () => [
      {
        name: "Add to Collection",
        icon: <PlaylistAddIcon fontSize="small" />,
        action: ({ selected }) => {
          setSelectedEventsForCollection(
            new Set(selected.map((e) => e.event_id)),
          );
          setAddToCollectionOpen(true);
        },
      },
      {
        name: "Delete",
        icon: <DeleteOutlineIcon fontSize="small" />,
        action: ({ selected }) => {
          setEventsToDelete(selected.map((e) => e.event_id));
          setDeleteConfirmOpen(true);
        },
      },
    ],
    [],
  );

  const handleDeleteConfirm = async () => {
    try {
      await deleteEvents(eventsToDelete);
    } catch {
      showNotification(
        `${eventsToDelete.length} event${eventsToDelete.length !== 1 ? "s" : ""} could not be deleted`,
        "error",
      );
    }
  };

  return (
    <PageLayout title="Roboto - Events" headerContentLeft="Events">
      <EventsList scope={{ type: "org" }} selectionOptions={selectionOptions} />
      <Dialog
        open={addToCollectionOpen}
        onClose={() => setAddToCollectionOpen(false)}
      >
        <AddToCollection
          selected={{ type: "Event", items: selectedEventsForCollection }}
          onCancel={() => setAddToCollectionOpen(false)}
        />
      </Dialog>
      <AlertDialog
        dialogOpen={deleteConfirmOpen}
        handleClose={() => {
          setDeleteConfirmOpen(false);
          setEventsToDelete([]);
        }}
        dialogTitle={`Delete ${eventsToDelete.length} event${eventsToDelete.length !== 1 ? "s" : ""}?`}
        dialogText="This action cannot be undone."
        dialogAction={handleDeleteConfirm}
      />
    </PageLayout>
  );
}
